import { Checkbox, Space, Select, Input, Alert, Typography, Popconfirm } from 'antd'
import './DownPayment.css'
import { setAutoSubmitDownPaymentInvoice, dueDateOptions, DueDateOption, numberToDueDateOption, dueDateOptionToNumber, setPaymentDueInDays, isAutoSubmitDisabled, Action, DownPaymentParameters } from './proposalsModel'
import { useContext, useEffect, useState } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import PaymentMethods, { PaymentMethodOnFileSettings } from './PaymentOptions/PaymentMethods'
import DownPaymentInvoiceDescriptions from './DownPaymentInvoiceDescriptions'
import { LineItem } from './lineItemsModel'

const { Option } = Select
const { Link } = Typography

type Props = {
  downPayment: DownPaymentParameters
  lineItems: LineItem[]
  includesTimeAndMaterialsOrFlatRate: boolean
  onLinkCustomerToQbo?: (shouldSave: boolean) => void,
  onLinkCostCodeToQbo?: (costCodeId: number, shouldSave: boolean) => void,
  dispatch: (a: Action) => void,
}

function DownPayment({ downPayment, lineItems, onLinkCustomerToQbo = () => { }, onLinkCostCodeToQbo = () => { }, dispatch, includesTimeAndMaterialsOrFlatRate }: Props) {
  const [selectedOption, setSelectedOption] = useState(numberToDueDateOption(downPayment.paymentDueInDays || 0))
  const { currentUser } = useContext(AuthenticationContext)

  const onAutoSubmitChanged = (value: boolean) => dispatch(setAutoSubmitDownPaymentInvoice(value))

  const selectedOptionChanged = (option: DueDateOption) => {
    setSelectedOption(option)
    if (option !== DueDateOption.Custom) {
      dispatch(setPaymentDueInDays(dueDateOptionToNumber(option)))
    }
  }

  const shouldCheckQuickBooksLinks = downPayment.invoicingSystem === 'quickBooks' || (downPayment.invoicingSystem === 'synkedUp' && currentUser?.isTenantConnectedToQuickBooks === true)
  const isMissingQboLinks = shouldCheckQuickBooksLinks && isAutoSubmitDisabled(downPayment.isCustomerLinkedToQbo, downPayment.costCodesUnlinkedToQbo)
  const showTimeAndMaterialsOrFlatRateMessage = includesTimeAndMaterialsOrFlatRate
  const autoSubmitDisabled = isMissingQboLinks || showTimeAndMaterialsOrFlatRateMessage
  useEffect(() => {
    if (autoSubmitDisabled) {
      dispatch(setAutoSubmitDownPaymentInvoice(false))
    }
  }, [autoSubmitDisabled])

  return <>
    <Space direction='vertical'>
      <Space>
        <Checkbox checked={!autoSubmitDisabled && downPayment.autoSubmitDownPaymentInvoice} onChange={e => onAutoSubmitChanged(e.target.checked)} disabled={autoSubmitDisabled}>
          Auto-Submit Down-Payment Invoice upon Customer Approval
        </Checkbox>

        {downPayment.autoSubmitDownPaymentInvoice &&
          <Select
            defaultValue={selectedOption}
            style={{ width: '160px' }}
            onChange={value => selectedOptionChanged(value as number)}
            data-testid="downPayment.paymentDueInDaysSelect">
            {
              dueDateOptions.map(([display, n]) => (
                <Option value={n} key={n}>{display}</Option>
              ))
            }
          </Select>
        }
        {downPayment.autoSubmitDownPaymentInvoice && selectedOption === DueDateOption.Custom &&
          <Input
            defaultValue={downPayment.paymentDueInDays}
            addonAfter="Days"
            style={{ width: '120px' }}
            type="number"
            max={1000}
            min={0}
            onChange={e => dispatch(setPaymentDueInDays(e.target.valueAsNumber))}
            data-testid="downPayment.paymentDueInDaysCustom"
          />
        }
      </Space>

      {isMissingQboLinks &&
        <Alert type="warning" showIcon message="Invoicing Requires QuickBooks Links" description={
          <ul style={{ padding: 0 }}>
            {!downPayment.isCustomerLinkedToQbo && <li>
              <Popconfirm
                title="Would you like to save your progress on this proposal before linking the customer to QuickBooks Online?"
                onConfirm={() => onLinkCustomerToQbo(true)}
                onCancel={() => onLinkCustomerToQbo(false)}
                okText="Yes"
                cancelText="No"
              >
                <Link>Link Customer</Link>
              </Popconfirm>
            </li>}
            {downPayment.costCodesUnlinkedToQbo.map(x => <li key={`cost-code-${x.id}`}>
              <Popconfirm
                title="Would you like to save your progress on this proposal before linking this cost code to QuickBooks Online?"
                onConfirm={() => onLinkCostCodeToQbo(x.id, true)}
                onCancel={() => onLinkCostCodeToQbo(x.id, false)}
                okText="Yes"
                cancelText="No"
              >
              <Link>Link cost code <strong>{x.name}</strong></Link>
              </Popconfirm>
            </li>)}
          </ul>
        } />
      }

      {showTimeAndMaterialsOrFlatRateMessage &&
          <Alert type="info" showIcon message="Auto-Submit is not available when the proposal includes Time & Materials work areas." />
      }

      {!autoSubmitDisabled && downPayment.autoSubmitDownPaymentInvoice &&
        <DownPaymentInvoiceDescriptions lineItems={lineItems} dispatch={dispatch} />
      }
    </Space>

    {downPayment.autoSubmitDownPaymentInvoice &&
      <Space direction='vertical'>
        <PaymentMethods downPayment={downPayment} dispatch={dispatch} />
        <PaymentMethodOnFileSettings downPayment={downPayment} dispatch={dispatch} />
      </Space>
    }
  </>
}

export default DownPayment
